import { EIcon } from '@NGComponents/NGIcon/NGIcon.enum';
import { EAvatarItemType, EAvatarType, EItemRarity } from '@NGUtils/enums';
import { getEnumFromValue } from '@NGUtils/functions';
import { IItem, IPreviewWornItem } from '@characterInventory/components/Avatar/Avatar.type';
import {
	IItemResponse,
	IMediaFormats,
	ISrcMedia,
	IWornItemsContentResponse,
	TUserInventoryGetResponse
} from '@services/inventory/inventory.services.type';
import { BASE_AVATAR_ITEMS } from './characterStore.variables';

function sortByType(a: IItem, b: IItem) {
	if (a.type && b.type) {
		if (a.type === EAvatarItemType.onHead && b.type !== EAvatarItemType.onHead) {
			return -1;
		}
		if (a.type !== EAvatarItemType.onHead && b.type === EAvatarItemType.onHead) {
			return 1;
		}
		if (a.type === EAvatarItemType.onChest && b.type !== EAvatarItemType.onChest) {
			return -1;
		}
		if (a.type !== EAvatarItemType.onChest && b.type === EAvatarItemType.onChest) {
			return 1;
		}
		if (a.type === EAvatarItemType.onHands && b.type !== EAvatarItemType.onHands) {
			return -1;
		}
		if (a.type !== EAvatarItemType.onHands && b.type === EAvatarItemType.onHands) {
			return 1;
		}
		if (a.type === EAvatarItemType.onLegs && b.type !== EAvatarItemType.onLegs) {
			return -1;
		}
		if (a.type !== EAvatarItemType.onLegs && b.type === EAvatarItemType.onLegs) {
			return 1;
		}
		if (a.type === EAvatarItemType.inRHand && b.type !== EAvatarItemType.inRHand) {
			return -1;
		}
		if (a.type !== EAvatarItemType.inRHand && b.type === EAvatarItemType.inRHand) {
			return 1;
		}
		if (a.type === EAvatarItemType.inLHand && b.type !== EAvatarItemType.inLHand) {
			return -1;
		}
		if (a.type !== EAvatarItemType.inLHand && b.type === EAvatarItemType.inLHand) {
			return 1;
		}
	}

	return 0;
}

function sortByRarity(a: IItem, b: IItem) {
	const rarityOrder = [
		EItemRarity.common,
		EItemRarity.uncommon,
		EItemRarity.rare,
		EItemRarity.epic,
		EItemRarity.legendary,
		EItemRarity.unique
	];

	if (a.rarity && b.rarity) {
		const aRarityIndex = rarityOrder.indexOf(a.rarity);
		const bRarityIndex = rarityOrder.indexOf(b.rarity);

		if (aRarityIndex > bRarityIndex) {
			return 1;
		} else if (aRarityIndex < bRarityIndex) {
			return -1;
		}
	}

	return 0;
}

function sortByCategory(a: IItem, b: IItem) {
	if (a.category && b.category) {
		if (a.category < b.category) {
			return -1;
		}
		if (a.category > b.category) {
			return 1;
		}
	}

	return 0;
}

// Sort by category, then by type (head, chest, hands, legs, right and left hand), then by rarity
function sortInventoryItems(items: IItem[]) {
	return items.sort((a: IItem, b: IItem) => {
		return sortByCategory(a, b) || sortByType(a, b) || sortByRarity(a, b);
	});
}

function convertItemCountToItemWithCount(items: TUserInventoryGetResponse): IItemResponse[] {
	return items.map(({ item, count }) => ({
		...item,
		count
	}));
}

function getItemsFromWornItemsResponse(wornItems: IWornItemsContentResponse['wornItems']): IItemResponse[] {
	// remove all values that are not IItemResponse
	return Object.values(wornItems).filter((item) => item !== null && typeof item !== 'number') as IItemResponse[];
}

function formatFetchedItems(items: IItemResponse[], avatarType: EAvatarType): IItem[] {
	const isFemale = avatarType === EAvatarType.female;

	return items.map((item: IItemResponse): IItem => {
		let {
			id,
			type,
			label,
			description,
			srcItemM,
			srcItemMBack,
			srcItemF,
			srcItemFBack,
			srcPreviewM,
			srcPreviewF,
			color,
			srcPreviewMBack,
			srcPreviewFBack,
			srcPreviewFCropped,
			srcPreviewMCropped,
			rarity,
			zIndex,
			zIndex2,
			category,
			isReference,
			count
		} = item;

		// set fallback if does not exist for female
		if (!srcPreviewF?.formats?.small?.url) {
			srcPreviewF = {} as ISrcMedia;
			srcPreviewF.formats = srcPreviewM?.formats;
		}
		if (srcPreviewMBack?.formats?.small?.url && !srcPreviewFBack?.formats?.small?.url) {
			srcPreviewFBack = {} as ISrcMedia;
			srcPreviewFBack!.formats = srcPreviewMBack?.formats;
		}

		// set fallback if does not exist for female
		if (!srcItemF?.formats?.small?.url) {
			// srcItemF.formats = srcItemM?.formats;
		}
		if (srcItemMBack?.formats?.small?.url && !srcItemFBack?.formats?.small?.url) {
			srcItemFBack = {} as ISrcMedia;
			srcItemFBack!.formats = srcItemMBack?.formats;
		}

		// set fallback if does not exist for female
		if (!srcPreviewFCropped?.formats?.small?.url) {
			srcPreviewFCropped = {} as ISrcMedia;
			srcPreviewFCropped.formats = srcPreviewMCropped?.formats;
		}

		return {
			id: id,
			type: getEnumFromValue(type, EAvatarItemType),
			layer: zIndex,
			layer2: zIndex2,
			label,
			description: description || '',
			rarity: getEnumFromValue(rarity, EItemRarity),
			displayedAssets: {
				srcPreview: isFemale
					? srcPreviewF?.formats || ({} as IMediaFormats)
					: srcPreviewM?.formats || ({} as IMediaFormats),
				srcPreviewBack: isFemale
					? srcPreviewFBack?.formats || ({} as IMediaFormats)
					: srcPreviewMBack?.formats || ({} as IMediaFormats),
				srcPreviewCropped: isFemale
					? srcPreviewFCropped?.formats || ({} as IMediaFormats)
					: srcPreviewMCropped?.formats || ({} as IMediaFormats),
				srcItem: isFemale ? srcItemF?.formats || ({} as IMediaFormats) : srcItemM?.formats || ({} as IMediaFormats),
				srcItemBack: isFemale
					? srcItemFBack?.formats || ({} as IMediaFormats)
					: srcItemMBack?.formats || ({} as IMediaFormats)
			},
			allAssets: {
				srcPreviewM: srcPreviewM?.formats || null,
				srcPreviewMBack: srcPreviewMBack?.formats || null,
				srcPreviewMCropped: srcPreviewMCropped?.formats || null,
				srcItemM: srcItemM?.formats || null,
				srcItemMBack: srcItemMBack?.formats || null,
				srcPreviewF: srcPreviewF?.formats || null,
				srcPreviewFBack: srcPreviewFBack?.formats || null,
				srcPreviewFCropped: srcPreviewFCropped?.formats || null,
				srcItemF: srcItemF?.formats || null,
				srcItemFBack: srcItemFBack?.formats || null
			},
			isReference: isReference,
			category: category,
			color: color,
			count: count || NaN
		};
	});
}

function formatFetchedWornItems(wornItems: IWornItemsContentResponse['wornItems'], avatarType: EAvatarType): IItem[] {
	if (!wornItems) return [];

	try {
		const itemsToFormat = getItemsFromWornItemsResponse(wornItems);

		return formatFetchedItems(itemsToFormat, avatarType);
	} catch (err) {
		console.error('formatFetchedWornItems', err);
		return [];
	}
}

function formatFetchedInventoryItems(
	fetchedInventoryItems: TUserInventoryGetResponse,
	avatarType: EAvatarType
): IItem[] {
	try {
		if (!fetchedInventoryItems) {
			return [];
		}

		const itemsToFormat = convertItemCountToItemWithCount(fetchedInventoryItems);

		const itemsFormatted = formatFetchedItems(itemsToFormat, avatarType);
		return sortInventoryItems(itemsFormatted);
	} catch (err) {
		console.error('formattedFetchedItems', err);
		return [];
	}
}

function updateDisplayedAssets(items: IItem[], avatarType: EAvatarType) {
	try {
		const isFemale = avatarType === EAvatarType.female;

		return items.map((item) => ({
			...item,
			displayedAssets: {
				srcPreview:
					(isFemale
						? item.allAssets.srcPreviewF || (item.type !== EAvatarItemType.beard ? item.allAssets.srcPreviewM : null)
						: item.allAssets.srcPreviewM) || ({} as IMediaFormats),
				srcPreviewBack: isFemale
					? item.allAssets.srcPreviewFBack ||
					  (item.type !== EAvatarItemType.beard ? item.allAssets.srcPreviewMBack : null)
					: item.allAssets.srcPreviewMBack,
				srcPreviewCropped:
					(isFemale
						? item.allAssets.srcPreviewFCropped ||
						  (item.type !== EAvatarItemType.beard ? item.allAssets.srcPreviewMCropped : null)
						: item.allAssets.srcPreviewMCropped) || ({} as IMediaFormats),
				srcItem:
					(isFemale
						? item.allAssets.srcItemF || (item.type !== EAvatarItemType.beard ? item.allAssets.srcItemM : null)
						: item.allAssets.srcItemM) || ({} as IMediaFormats),
				srcItemBack: isFemale
					? item.allAssets.srcItemFBack || (item.type !== EAvatarItemType.beard ? item.allAssets.srcItemMBack : null)
					: item.allAssets.srcItemMBack
			}
		}));
	} catch {
		return items;
	}
}

function convertItemToPreviewWornItem(wornItems: IItem[]): IPreviewWornItem[] {
	let formattedWornItems = wornItems
		.filter((item) => !item.isReference)
		.map((item: IItem): IPreviewWornItem => {
			const { order, icon } = BASE_AVATAR_ITEMS.find((avatarItem) => avatarItem.type === item.type) || {
				order: 0,
				icon: EIcon.none
			};

			return {
				id: item.id,
				type: item.type,
				label: item.label,
				description: item.description,
				layer: item.layer || 0,
				layer2: item.layer2 || 0,
				srcPreviewCropped: item.displayedAssets.srcPreviewCropped,
				rarity: item.rarity,
				icon,
				order
			};
		});

	// put items from BASE_AVATAR_ITEMS into the formattedWornItems when type doesn't exists to show empty slots
	formattedWornItems = formattedWornItems.concat(
		BASE_AVATAR_ITEMS.filter((avatarItem) => !formattedWornItems.find((item) => item.type === avatarItem.type))
	);

	// Sort from Helmet to Legs
	formattedWornItems.sort((a, b) => a.order - b.order);

	return formattedWornItems.reverse();
}

export {
	convertItemCountToItemWithCount,
	getItemsFromWornItemsResponse,
	formatFetchedItems,
	formatFetchedWornItems,
	formatFetchedInventoryItems,
	sortInventoryItems,
	sortByCategory,
	sortByType,
	sortByRarity,
	convertItemToPreviewWornItem,
	updateDisplayedAssets
};
